import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, Divider, IconButton, InputAdornment, LinearProgress, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import TollOutlinedIcon from '@mui/icons-material/TollOutlined';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiRequest } from '../Utils/apiClient';
import { getSaasOrganization } from '../Utils/saasOrganization';
import { useUser } from './WebSocketContext';
import SaasWelcome from './SaasWelcome';
import DialogHeading from './Common/DialogHeading';
import CloseButton from './Common/CloseButton';

const OrganizationWorkspace = ({ welcome = false, onClose }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { organizationId, switchOrganization, canManageProject, isReadOnly, isValidator } = useUser();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const loadSequence = useRef(0);
  const currentOrganization = useRef(organizationId);
  currentOrganization.current = organizationId;
  const [moreMembers, setMoreMembers] = useState(false);
  const invitationSwitchStarted = useRef(false);
  useEffect(() => {
    // Consume the one-time post-sign-in redirect as soon as its destination loads.
    sessionStorage.removeItem('dataunfold.saasWelcome');
    const invitedOrganization = sessionStorage.getItem('dataunfold.invitedOrganizationId');
    if (!invitedOrganization || !organizationId || invitationSwitchStarted.current) return;
    invitationSwitchStarted.current = true;
    sessionStorage.removeItem('dataunfold.invitedOrganizationId');
    if (String(invitedOrganization) !== String(organizationId)) {
      switchOrganization(invitedOrganization).catch(err => setError(err.message));
    }
  }, [organizationId, switchOrganization, welcome]);
  const load = useCallback(async () => {
    if (!organizationId || String(organizationId) !== String(currentOrganization.current)) return;
    const sequence = ++loadSequence.current;
    try {
      const result = await getSaasOrganization(organizationId);
      if (sequence === loadSequence.current && String(organizationId) === String(currentOrganization.current)) { setData(result); setMoreMembers(result.members.length === 100); setError(''); }
    } catch (err) { if (sequence === loadSequence.current && String(organizationId) === String(currentOrganization.current)) setError(err.message); }
  }, [organizationId]);
  useEffect(() => { setData(null); setError(''); setMessage(''); setEmail(''); setRole('viewer'); load(); return () => { loadSequence.current += 1; }; }, [load]);

  useEffect(() => {
    if (data?.state !== 'provisioning' || data.role !== 'owner') return undefined;
    const retryTimer = window.setInterval(load, 61_000);
    return () => window.clearInterval(retryTimer);
  }, [data?.state, data?.role, load]);

  const act = async (path, body, method = 'POST') => {
    if (busy || !data || String(data.organization_id) !== String(organizationId)) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const result = await apiRequest(`/api/v1/saas/organization/${path}`, { method, headers: { 'X-Organization-ID': String(organizationId) }, ...(body ? { body: JSON.stringify(body) } : {}) });
      if (String(organizationId) !== String(currentOrganization.current)) return;
      setMessage(t(`saas.${result.status || 'saved'}`));
      if (path === 'invitations') setEmail('');
      await load();
    } catch (err) { if (String(organizationId) === String(currentOrganization.current)) setError(err.message); }
    finally { setBusy(false); }
  };
  const loadMembers = async () => {
    const sequence = loadSequence.current;
    setBusy(true);
    try {
      const result = await apiRequest(`/api/v1/saas/organization/members?after_id=${data.members[data.members.length - 1].id}`, { headers: { 'X-Organization-ID': String(organizationId) } });
      if (sequence === loadSequence.current) { setData(previous => ({ ...previous, members: [...previous.members, ...result.members] })); setMoreMembers(result.members.length === 100); }
    } catch (err) { if (String(organizationId) === String(currentOrganization.current)) setError(err.message); }
    finally { setBusy(false); }
  };
  const canManage = data && ['owner', 'admin'].includes(data.role);
  const expired = data?.trial_expires_at && data.trial_expires_at * 1000 <= Date.now();
  const trialBlocked = data?.state === 'trial' && (expired || data.credits <= 0);
  const close = onClose || (() => navigate('/project-dashboard', { replace: true }));
  const locale = i18n?.resolvedLanguage || i18n?.language;

  if (welcome) return <SaasWelcome data={data} busy={busy} error={error}
    canCreate={Boolean(canManageProject && !isReadOnly && !isValidator)} trialBlocked={trialBlocked} expired={expired}
    onRetryTrial={() => act('start-trial')} onReload={load} />;

  return <Dialog open onClose={(_, reason) => { if (reason !== 'backdropClick') close(); }} fullWidth maxWidth="sm"
    aria-labelledby="organization-dialog-title" sx={{ '& .MuiDialogTitle-root': { pr: 8 } }}>
    <DialogHeading id="organization-dialog-title" icon={BusinessOutlinedIcon}
      title={data?.name || t('saas.workspace')} description={data ? t('saas.workspace') : undefined} />
    <CloseButton onClick={close} ariaLabel={t('close')} tooltip={t('close')} sx={{ position: 'absolute', top: 20, right: 16 }} />
    <DialogContent dividers aria-busy={busy} sx={{ position: 'relative' }}>
    {busy && <LinearProgress aria-label={t('common.loading')} sx={{ position: 'absolute', top: 0, left: 0, right: 0 }} />}
    <Stack spacing={2.5}>
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {!data ? error
        ? <Button onClick={load}>{t('saas.welcome_reload')}</Button>
        : <LinearProgress aria-label={t('saas.welcome_loading')} /> : <>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
          <Chip size="small" variant="outlined" color={data.state === 'active' ? 'success' : 'default'}
            icon={data.state === 'active' ? <CheckCircleOutlineIcon /> : <ScheduleOutlinedIcon />} label={t(`saas.state_${data.state}`)} />
          <Chip size="small" variant="outlined" label={t(`saas.role_${data.role}`)} />
        </Stack>
        <Box sx={{ p: 2, borderRadius: 2, bgcolor: 'action.hover', border: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TollOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{t('saas.credits_available')}</Typography>
            {data.state === 'trial' && <Tooltip describeChild title={<>{t('saas.shared_trial')}<br />{t('saas.trial_cost_help')}</>}>
              <IconButton size="small" aria-label={t('saas.trial_details')}><InfoOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>}
          </Stack>
          <Typography component="p" aria-label={t('saas.credit_balance', { count: data.credits })}
            sx={{ mt: 0.75, fontSize: { xs: 28, sm: 34 }, fontWeight: 700, lineHeight: 1.2, fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}>
            {new Intl.NumberFormat(locale).format(data.credits)}
          </Typography>
          {data.state === 'trial' && <>
            <LinearProgress aria-label={t('saas.credits_available')} variant="determinate" value={Math.max(0, Math.min(100, data.credits / 5))} sx={{ my: 1.5, height: 5, borderRadius: 4 }} />
            <Typography variant="caption" color="text.secondary">{t('saas.expires', { date: new Date(data.trial_expires_at * 1000).toLocaleDateString(locale) })}</Typography>
          </>}
          {!data.balance_current && <Alert severity="info" sx={{ mt: 2 }}>{t('saas.cached_balance')}</Alert>}
          {trialBlocked && <Alert severity="warning" sx={{ mt: 2 }}>{t(expired ? 'saas.trial_expired' : 'saas.trial_exhausted')}</Alert>}
          {data.state === 'provisioning' && data.role === 'owner' && <Button sx={{ mt: 2 }} variant="contained" disabled={busy} onClick={() => act('start-trial')}>{t('saas.retry_trial')}</Button>}
          {['trial', 'pending_activation'].includes(data.state) && data.role === 'owner' && <Button sx={{ mt: 2 }} variant="contained" disabled={busy || Boolean(data.activation_requested_at)} onClick={() => act('request-activation')}>{t(data.activation_requested_at ? 'saas.activation_pending' : 'saas.request_activation')}</Button>}
          {data.state === 'active' && canManage && <>
            <Tooltip describeChild title={t('saas.resume_processing_help')}>
              <span><Button sx={{ mt: 1.5 }} size="small" variant="outlined" startIcon={<PlayArrowRoundedIcon />} disabled={busy} onClick={() => act('resume-processing')}>{t('saas.resume_processing')}</Button></span>
            </Tooltip>
          </>}
        </Box>
        {canManage && <>
          <Divider />
          <Stack direction="row" alignItems="center" spacing={1}>
            <PeopleOutlineIcon color="primary" fontSize="small" />
            <Typography component="h3" variant="subtitle1" sx={{ flex: 1, fontWeight: 600 }}>{t('saas.members')}</Typography>
            <Tooltip title={t('saas.manage_permissions')}>
              <IconButton component={Link} to="/Settings" aria-label={t('saas.manage_permissions')} size="small"><SettingsOutlinedIcon fontSize="small" /></IconButton>
            </Tooltip>
          </Stack>
          <Stack component="form" spacing={1.5} onSubmit={event => { event.preventDefault(); act('invitations', { email, role }); }}>
            <TextField size="small" label={t('saas.email')} type="email" required value={email} onChange={e => setEmail(e.target.value)} fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start"><MailOutlineIcon fontSize="small" /></InputAdornment> }} />
            <Stack direction="row" spacing={1.5}>
              <TextField size="small" select label={t('saas.role')} value={role} onChange={e => setRole(e.target.value)} sx={{ flex: 1, minWidth: 0 }}>{['admin', 'editor', 'viewer'].map(value => <MenuItem key={value} value={value}>{t(`saas.role_${value}`)}</MenuItem>)}</TextField>
              <Button type="submit" variant="contained" startIcon={<PersonAddAltOutlinedIcon />} disabled={busy || !email.trim()}>{t('saas.invite')}</Button>
            </Stack>
          </Stack>
          <Stack spacing={1.5} sx={{ maxHeight: 200, overflowY: 'auto' }}>
          {data.members.map(member => <Stack key={member.id} direction="row" alignItems="center" spacing={1.25}>
            <Avatar aria-hidden="true" sx={{ width: 32, height: 32, fontSize: 14, bgcolor: 'action.selected', color: 'primary.main' }}>{Array.from(member.email || member.username || '?')[0].toLocaleUpperCase(locale)}</Avatar>
            <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>{member.email || member.username}</Typography><Chip size="small" variant="outlined" label={t(`saas.role_${member.organization_role}`)} />
          </Stack>)}
          {moreMembers && <Button disabled={busy} onClick={loadMembers}>{t('saas.load_more')}</Button>}
          {data.invitations.map(invitation => <Stack key={invitation.id} direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'action.hover', color: 'text.secondary' }}><ScheduleOutlinedIcon fontSize="small" /></Avatar>
            <Box sx={{ flex: 1, minWidth: 0 }}><Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{invitation.email}</Typography><Typography variant="caption" color="text.secondary">{t('saas.invited')}</Typography></Box>
            <Tooltip title={t('saas.revoke')}><span><IconButton disabled={busy} aria-label={t('saas.revoke_invitation', { email: invitation.email })} onClick={() => act(`invitations/${invitation.id}`, null, 'DELETE')} size="small"><CloseRoundedIcon fontSize="small" /></IconButton></span></Tooltip>
          </Stack>)}
          </Stack>
        </>}
      </>}
    </Stack>
    </DialogContent>
    <DialogActions>
      <Button component={Link} to="/project-dashboard" aria-label={t('saas.view_projects')} startIcon={<FolderOutlinedIcon />}>{t('saas.projects')}</Button>
      {canManage && canManageProject && !isReadOnly && !isValidator && <Tooltip title={t('saas.create_project')}>
        <IconButton component={Link} to="/add" aria-label={t('saas.create_project')} color="primary"><AddRoundedIcon /></IconButton>
      </Tooltip>}
      <Box sx={{ flex: 1 }} />
      {data?.platform_admin && <Tooltip title={t('saas.platform_title')}>
        <IconButton component={Link} to="/platform" aria-label={t('saas.platform_title')} color="primary"><AdminPanelSettingsOutlinedIcon /></IconButton>
      </Tooltip>}
    </DialogActions>
  </Dialog>;
};

export default OrganizationWorkspace;
